import { HttpClient } from '../api/HttpClient';
import type {
  User,
  GetUsersRequest,
  GetUsersResponse,
  UpdateUserRequest,
  CreateUserRequest,
} from '../../types/api/user';
import type {
  AdminUsersData,
  AdminUsersQueryParams,
  AdminUserDetail,
  UserUpdateRequest,
  UserSoftDeleteRequest,
  UserSoftDeleteResponse,
  UserRestoreResponse,
  DeletedUsersData,
} from '../../types/api/admin-user';
import type {
  TvUserOnboardingRequest,
  TvUserOnboardingResponse,
} from '../../types/api/tv-user';

/**
 * User Service
 * 
 * Handles all user-related API operations for admin functionality
 * 
 * Features:
 * - User listing with pagination and filtering
 * - User CRUD operations
 * - User status management
 * - Search functionality
 * - Role management
 */
export class UserService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = HttpClient.getInstance();
  }

  /**
   * Get users list with pagination and filters
   */
  async getUsers(request: GetUsersRequest = {}): Promise<GetUsersResponse> {
    const params = new URLSearchParams();
    
    // Add pagination parameters
    if (request.page) params.append('page', request.page.toString());
    if (request.limit) params.append('limit', request.limit.toString());
    
    // Add filter parameters
    if (request.role) params.append('role', request.role);
    if (request.status) params.append('status', request.status);
    if (request.emailVerified !== undefined && request.emailVerified !== null) {
      params.append('emailVerified', request.emailVerified.toString());
    }
    if (request.phoneVerified !== undefined && request.phoneVerified !== null) {
      params.append('phoneVerified', request.phoneVerified.toString());
    }
    if (request.searchTerm) params.append('searchTerm', request.searchTerm);
    if (request.sortBy) params.append('sortBy', request.sortBy);
    if (request.sortOrder) params.append('sortOrder', request.sortOrder);

    const url = `/users/admin/list${params.toString() ? `?${params.toString()}` : ''}`;
    
    const response = await this.httpClient.get<GetUsersResponse>(url);
    return response.data;
  }

  /**
   * Get user by ID
   */
  async getUser(userId: string): Promise<{ data: User }> {
    const response = await this.httpClient.get<{ data: User }>(`/users/${userId}`);
    return response.data;
  }

  /**
   * Create a new user
   */
  async createUser(userData: CreateUserRequest): Promise<{ data: User }> {
    const response = await this.httpClient.post<{ data: User }>('/users/admin/create', userData);
    return response.data;
  }

  /**
   * Update user information
   */
  async updateUser(userId: string, updateData: UpdateUserRequest): Promise<{ data: User }> {
    const response = await this.httpClient.put<{ data: User }>(`/users/${userId}`, updateData);
    return response.data;
  }

  /**
   * Delete a user
   */
  async deleteUser(userId: string): Promise<{ message: string }> {
    const response = await this.httpClient.delete<{ message: string }>(`/users/${userId}`);
    return response.data;
  }

  /**
   * Toggle user status (Active/Inactive)
   */
  async toggleUserStatus(userId: string): Promise<{ data: User }> {
    const response = await this.httpClient.patch<{ data: User }>(`/users/${userId}/status`);
    return response.data;
  }

  /**
   * Update user role
   */
  async updateUserRole(userId: string, role: string): Promise<{ data: User }> {
    const response = await this.httpClient.patch<{ data: User }>(`/users/${userId}/role`, { role });
    return response.data;
  }

  /**
   * Verify user email
   */
  async verifyUserEmail(userId: string): Promise<{ data: User }> {
    const response = await this.httpClient.patch<{ data: User }>(`/users/${userId}/verify-email`);
    return response.data;
  }

  /**
   * Verify user phone
   */
  async verifyUserPhone(userId: string): Promise<{ data: User }> {
    const response = await this.httpClient.patch<{ data: User }>(`/users/${userId}/verify-phone`);
    return response.data;
  }

  /**
   * Suspend user account
   */
  async suspendUser(userId: string, reason?: string): Promise<{ data: User }> {
    const response = await this.httpClient.patch<{ data: User }>(`/users/${userId}/suspend`, { reason });
    return response.data;
  }

  /**
   * Unsuspend user account
   */
  async unsuspendUser(userId: string): Promise<{ data: User }> {
    const response = await this.httpClient.patch<{ data: User }>(`/users/${userId}/unsuspend`);
    return response.data;
  }

  /**
   * Get user statistics
   */
  async getUserStats(): Promise<{
    data: {
      totalUsers: number;
      activeUsers: number;
      inactiveUsers: number;
      pendingVerification: number;
      suspendedUsers: number;
      usersByRole: Record<string, number>;
      recentSignups: number;
    };
  }> {
    const response = await this.httpClient.get<{
      data: {
        totalUsers: number;
        activeUsers: number;
        inactiveUsers: number;
        pendingVerification: number;
        suspendedUsers: number;
        usersByRole: Record<string, number>;
        recentSignups: number;
      };
    }>('/users/admin/stats');
    return response.data;
  }

  /**
   * Export users to CSV
   */
  async exportUsers(filters?: GetUsersRequest): Promise<Blob> {
    const params = new URLSearchParams();
    
    if (filters?.role) params.append('role', filters.role);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.emailVerified !== undefined && filters.emailVerified !== null) {
      params.append('emailVerified', filters.emailVerified.toString());
    }
    if (filters?.phoneVerified !== undefined && filters.phoneVerified !== null) {
      params.append('phoneVerified', filters.phoneVerified.toString());
    }
    if (filters?.searchTerm) params.append('searchTerm', filters.searchTerm);

    const url = `/users/admin/export${params.toString() ? `?${params.toString()}` : ''}`;
    
    const response = await this.httpClient.get(url, {
      headers: {
        'Accept': 'text/csv',
      },
    } as any);
    
    return response.data;
  }

  /**
   * Bulk update user status
   */
  async bulkUpdateUserStatus(userIds: string[], status: string): Promise<{ data: { updated: number } }> {
    const response = await this.httpClient.patch<{ data: { updated: number } }>('/users/admin/bulk-update-status', {
      userIds,
      status,
    });
    return response.data;
  }

  /**
   * Bulk delete users
   */
  async bulkDeleteUsers(userIds: string[]): Promise<{ data: { deleted: number } }> {
    const response = await this.httpClient.delete<{ data: { deleted: number } }>('/users/admin/bulk-delete', {
      data: { userIds },
    });
    return response.data;
  }

  /**
   * Get admin users list with pagination and filters
   * This endpoint matches the actual backend API: /admin/users
   */
  async getAdminUsers(params: AdminUsersQueryParams = {}): Promise<AdminUsersData> {
    const queryParams = new URLSearchParams();
    
    // Add filter parameters
    if (params.name) queryParams.append('name', params.name);
    if (params.email) queryParams.append('email', params.email);
    if (params.phone) queryParams.append('phone', params.phone);
    if (params.tier) queryParams.append('tier', params.tier);
    if (params.registration_date_from) {
      queryParams.append('registration_date_from', params.registration_date_from);
    }
    if (params.registration_date_to) {
      queryParams.append('registration_date_to', params.registration_date_to);
    }
    
    queryParams.append('page', (params.page ?? 0).toString());
    queryParams.append('size', (params.size ?? 20).toString());

    const url = `/admin/users?${queryParams.toString()}`;
    const response = await this.httpClient.get<AdminUsersData>(url);
    
    // Check if API request was successful
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch users');
    }
    
    // Return response.data directly since it contains { items, page, size, totalItems, ... }
    return response.data;
  }

  /**
   * Get admin user detail by ID (with audit fields)
   * Endpoint: GET /admin/users/{userId}
   */
  async getAdminUserDetail(userId: string): Promise<AdminUserDetail> {
    const response = await this.httpClient.get<AdminUserDetail>(
      `/admin/users/${userId}`
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch user detail');
    }
    
    return response.data;
  }

  /**
   * Update admin user information with optional profile image
   * Endpoint: PUT /admin/users/{userId}
   * Content-Type: multipart/form-data
   */
  async updateAdminUser(
    userId: string, 
    updateData: UserUpdateRequest, 
    profileImage?: File | null
  ): Promise<AdminUserDetail> {
    const formData = new FormData();
    
    // Create a JSON Blob for the data part (not just a string)
    // This matches the pattern used in CastService and other multipart uploads
    const jsonBlob = new Blob([JSON.stringify(updateData)], {
      type: 'application/json',
    });
    formData.append('data', jsonBlob, 'data.json');
    
    // Add profile image if provided
    if (profileImage) {
      formData.append('profileImage', profileImage, profileImage.name);
    }
    
    // Don't manually set Content-Type header - browser will set it automatically with boundary
    const response = await this.httpClient.put<AdminUserDetail>(
      `/admin/users/${userId}`,
      formData
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to update user');
    }
    
    return response.data;
  }

  /**
   * Soft delete a user (marks as deleted but preserves data)
   * Endpoint: DELETE /admin/users/{userId}
   */
  async softDeleteUser(userId: string, reason: string): Promise<UserSoftDeleteResponse> {
    const requestData: UserSoftDeleteRequest = { reason };
    
    const response = await this.httpClient.delete<UserSoftDeleteResponse>(
      `/admin/users/${userId}`,
      { data: requestData }
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete user');
    }
    
    return response.data;
  }

  /**
   * Restore a soft-deleted user
   * Endpoint: POST /admin/users/{userId}/restore
   */
  async restoreUser(userId: string): Promise<UserRestoreResponse> {
    const response = await this.httpClient.post<UserRestoreResponse>(
      `/admin/users/${userId}/restore`
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to restore user');
    }
    
    return response.data;
  }

  /**
   * Get list of soft-deleted users
   * Endpoint: GET /admin/users/deleted
   */
  async getDeletedUsers(params: AdminUsersQueryParams = {}): Promise<DeletedUsersData> {
    const queryParams = new URLSearchParams();
    
    queryParams.append('page', (params.page ?? 1).toString());
    queryParams.append('size', (params.size ?? 20).toString());
    
    // Add sorting parameters if provided
    if (params.tier) queryParams.append('tier', params.tier);

    const url = `/admin/users/deleted?${queryParams.toString()}`;
    const response = await this.httpClient.get<DeletedUsersData>(url);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch deleted users');
    }
    
    return response.data;
  }

  /**
   * Check if a user is soft-deleted
   * Endpoint: GET /admin/users/{userId}/deleted-status
   */
  async checkUserDeletedStatus(userId: string): Promise<boolean> {
    const response = await this.httpClient.get<boolean>(
      `/admin/users/${userId}/deleted-status`
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to check user deleted status');
    }
    
    return response.data;
  }

  /**
   * Onboard a TV platform user
   * Endpoint: POST /admin/users/tv
   */
  async onboardTvUser(request: TvUserOnboardingRequest): Promise<TvUserOnboardingResponse> {
    const response = await this.httpClient.post<TvUserOnboardingResponse>(
      '/admin/users/tv',
      request
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to onboard TV user');
    }
    
    return response.data;
  }
}
